import React, { Fragment, useState } from 'react'
import { getISOWeek, parseISO } from 'date-fns'
import { useGridData, gridByBranch, getBranchValues } from 'tve/components/appointment_grid/hooks'
import { getEnv } from 'tve/helpers/env'
import ErrorBoundary from './error_boundary'
import Loading from './loading'
import NoEvents from './no_events'
import RequestError from './request_error'
import SlotGenerator, { defaultDebugData, slotGenerator } from './slot_generator'
import Switcher from './switcher'
import ValidationError from './validation_error'

const debugMode = getEnv('APPOINTMENT_GRID') === 'debug'

const Grid = ({
  loadSlots,
  untilDays = 0,
  errorMessage,
  value,
  onChange,
  selectBranch,
  renderHeader,
  renderFooter,
  showLoading = true,
  showRequestError = true,
  showNoEvents = true,
  showValidationError = true,
  phone,
  medium,
  weeksSlotsHistory
}) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [debugData, setDebugData] = useState(Object.freeze(defaultDebugData))
  const { data, loading, error, branchId, setBranchId } = useGridData(
    currentDate,
    untilDays,
    medium,
    debugMode ? slotGenerator(debugData) : loadSlots
  )
  const grid = gridByBranch(data, branchId, { displaySlotName: medium === 'bank' })

  // NOTE: we should only show branch differentiation in the UIs if the medium
  // is "bank", meaning, a physical bank/branch location.
  const branchValues = medium === 'bank' ? getBranchValues(data) : []

  const nextHandler = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (data.nextStartDate) setCurrentDate(parseISO(data.nextStartDate))
  }

  const previousHandler = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (data.previousStartDate) setCurrentDate(parseISO(data.previousStartDate))
  }

  const reloadCurrentDate = (event) => {
    if (event) event.preventDefault()
    setCurrentDate(new Date(currentDate.getTime()))
  }

  const gridKeys = Object.keys(grid || {})
  const noGrid = gridKeys.length === 0
  const noEvents = gridKeys.every((key) => {
    return grid[key].every((entry) => entry.available == false)
  })

  const weekNumber = getISOWeek(currentDate)
  //if at least one of the previous week has slots, enables the button
  const prevWeeksHaveSlots = weeksSlotsHistory.some((week) => week.weekNumber < weekNumber && week.hasSlots)

  return (
    <div className="atp-dates__table-wrapper">
      <ErrorBoundary
        error={
          <Fragment>
            {debugMode && (
              <SlotGenerator data={debugData} setData={setDebugData} reloadCurrentDate={reloadCurrentDate} />
            )}
            <RequestError reloadCurrentDate={reloadCurrentDate} phone={phone} />
          </Fragment>
        }>
        {typeof renderHeader === 'function' &&
          renderHeader({ data, currentDate, loading, error, noEvents, noGrid, grid })}
        {debugMode && <SlotGenerator data={debugData} setData={setDebugData} reloadCurrentDate={reloadCurrentDate} />}
        {loading ? (
          showLoading ? (
            <Loading />
          ) : null
        ) : error ? (
          showRequestError ? (
            <RequestError reloadCurrentDate={reloadCurrentDate} />
          ) : null
        ) : (
          <Fragment>
            {noEvents && showNoEvents && <NoEvents noGrid={noGrid} />}
            {!noGrid && grid && (
              <Switcher
                currentDate={currentDate}
                grid={grid}
                value={value}
                onChange={onChange}
                selectBranch={selectBranch}
                nextHandler={data.nextStartDate && nextHandler}
                previousHandler={prevWeeksHaveSlots === true && data.previousStartDate ? previousHandler : null}
                handleBranchId={setBranchId}
                branchId={branchId}
                branchValues={branchValues}
              />
            )}
            {showValidationError && <ValidationError errorMessage={errorMessage} />}
          </Fragment>
        )}

        {typeof renderFooter === 'function' &&
          renderFooter({ data, currentDate, loading, error, noEvents, noGrid, grid })}
      </ErrorBoundary>
    </div>
  )
}

export default Grid
